'use client';

import { Box } from '@terra/ui-styled-system';
import { HeadingPage, MetaDefault } from '@terra/ui-design-system';
import { useFormatter, useNow, useTranslations } from 'next-intl';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useTargetId, useTargetType } from '../../store/projectStore';
import { useTargetVisitScope } from '../../hooks/useTargetVisitScope';

const RELATIVE_TIME_REFRESH_MS = 60_000;

type OverviewWelcomeMessageProps = {
  lastVisitAt?: Date | null;
};

export function OverviewWelcomeMessage({
  lastVisitAt,
}: OverviewWelcomeMessageProps) {
  const t = useTranslations('home');
  const formatter = useFormatter();
  const now = useNow({ updateInterval: RELATIVE_TIME_REFRESH_MS });
  const targetId = useTargetId();
  const targetType = useTargetType();
  const { lastVisitAt: recordedLastVisitAt } = useTargetVisitScope({
    targetId: targetId ?? null,
    targetType,
  });
  const { name, isLoading: isUserLoading } = useCurrentUser();

  const visitedAt =
    lastVisitAt === undefined ? (recordedLastVisitAt ?? null) : lastVisitAt;
  const isValidVisit = visitedAt !== null && !isNaN(visitedAt.getTime());

  return (
    <Box w="full" alignSelf="flex-start">
      <HeadingPage color="fg.neutral.default">
        {isUserLoading || !name
          ? t('welcomeBackAnonymous')
          : t('welcomeBack', { name })}
      </HeadingPage>
      {isValidVisit ? (
        <MetaDefault color="fg.neutral.muted">
          {t('lastVisited', {
            relativeTime: formatter.relativeTime(visitedAt, now),
          })}
        </MetaDefault>
      ) : lastVisitAt === null ? (
        <MetaDefault color="fg.neutral.muted">{t('firstVisit')}</MetaDefault>
      ) : null}
    </Box>
  );
}
